import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { FormControlLabel, Switch, RadioGroup, Radio } from "@mui/material";
 
const CONNECTOR_TYPES = [
    { label: "REST API", value: "rest-api" },
    { label: "PostgreSQL", value: "postgresql" },
    { label: "Blob Storage", value: "blob-storage" },
    { label: "Databricks", value: "databricks" },
];
 
const ENDPOINT_PLACEHOLDERS = {
    "rest-api": "<api-url>/<filename.json>",
    "postgresql": "postgresql://user:password@host:5432/dbname",
    "blob-storage": "DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net",
    "databricks": "https://<workspace>.azuredatabricks.net",
};
 
const ENDPOINT_LABELS = {
    "rest-api": "API Endpoint",
    "postgresql": "Connection URL",
    "blob-storage": "Connection String",
    "databricks": "Workspace URL",
};

const ENDPOINT_HELP_TEXT = {
    "blob-storage": "Find this in Azure Portal → Storage Account → Access keys → Connection string.",
    "databricks": "Found in your Azure Databricks workspace URL.",
    "postgresql": "Format: postgresql://user:password@host:5432/dbname",
    "rest-api": null,
};
 
const CustomToolDialog = ({
    visible,
    toolName,
    apiEndpoint,
    apiKey,
    toolSchema,
    schemaError,
    onHide,
    onAddOrEdit,
    setToolName,
    setApiEndpoint,
    setApiKey,
    setToolSchema,
    toolDescription,
    setToolDescription,
    toolPolicy,
    setToolPolicy,
    authType,
    setAuthType,
    connectorType,
    setConnectorType,
    // Blob Storage extras
    blobContainerName,
    setBlobContainerName,
}) => {
    const [errors, setErrors] = useState({});
    const [isJsonSchema, setIsJsonSchema] = useState(false);
 
    const handleToggle = (event) => {
        setIsJsonSchema(event.target.checked);
    };
 
    const selectedConnector = connectorType || "rest-api";
 
    const validateFields = () => {
        let newErrors = {};
 
        if (!toolName || !toolName.trim())
            newErrors.toolName = "Tool name is required.";
        if (!toolDescription || !toolDescription.trim())
            newErrors.toolDescription = "Tool Description is required.";
        if (!toolPolicy || !toolPolicy.trim())
            newErrors.toolPolicy = "Tool Policy is required.";
        if (!apiEndpoint || !apiEndpoint.trim())
            newErrors.apiEndpoint = `${ENDPOINT_LABELS[selectedConnector]} is required.`;

        // Blob Storage: validate connection string format
        if (selectedConnector === "blob-storage" && apiEndpoint) {
            if (!apiEndpoint.includes("AccountName=") && !apiEndpoint.startsWith("DefaultEndpointsProtocol")) {
                newErrors.apiEndpoint = "Invalid connection string. It should start with 'DefaultEndpointsProtocol=https;...'";
            }
        }
 
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
 
    const handleSave = () => {
        if (validateFields()) {
            onAddOrEdit();
        }
    };

    const helpText = ENDPOINT_HELP_TEXT[selectedConnector];
 
    return (
        <Dialog header="Add/Edit Custom Tool" visible={visible} style={{ width: "450px" }} onHide={onHide}>
            <div className="form-group">
                <label htmlFor="connectorType">Connector Type <span style={{ color: "red" }}>*</span></label>
                <Dropdown
                    id="connectorType"
                    value={selectedConnector}
                    options={CONNECTOR_TYPES}
                    onChange={(e) => setConnectorType(e.value)}
                    placeholder="Select connector type"
                    style={{ width: "100%" }}
                />
            </div>
            <div className="form-group">
                <label htmlFor="toolName">Tool Name <span style={{ color: "red" }}>*</span></label>
                <InputText
                    id="toolName"
                    value={toolName}
                    onChange={(e) => setToolName(e.target.value)}
                    placeholder="Enter tool name"
                    className={errors.toolName ? "p-invalid" : ""}
                />
                {errors.toolName && <small className="p-error">{errors.toolName}</small>}
            </div>
            <div className="form-group">
                <label htmlFor="toolDescription">Tool Description <span style={{ color: "red" }}>*</span></label>
                <InputTextarea
                    id="toolDescription"
                    value={toolDescription}
                    onChange={(e) => setToolDescription(e.target.value)}
                    placeholder="Enter Description"
                    rows={3}
                    className={errors.toolDescription ? "p-invalid" : ""}
                />
                {errors.toolDescription && <small className="p-error">{errors.toolDescription}</small>}
            </div>
            <div className="form-group">
                <label htmlFor="toolPolicy">Tool Policy <span style={{ color: "red" }}>*</span></label>
                <InputTextarea
                    id="toolPolicy"
                    value={toolPolicy}
                    onChange={(e) => setToolPolicy(e.target.value)}
                    placeholder="Enter Policy"
                    rows={3}
                    className={errors.toolPolicy ? "p-invalid" : ""}
                />
                {errors.toolPolicy && <small className="p-error">{errors.toolPolicy}</small>}
            </div>
 
            {selectedConnector === "rest-api" && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ marginBottom: '5px' }}>API Endpoint</span>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isJsonSchema}
                                onChange={handleToggle}
                            />
                        }
                        label="JSON Schema"
                    />
                </div>
            )}
 
            {selectedConnector === "rest-api" && isJsonSchema ? (
                <div className="form-group">
                    <label htmlFor="toolSchema">JSON Schema <span style={{ color: "red" }}>*</span></label>
                    <InputTextarea
                        id="toolSchema"
                        value={toolSchema}
                        onChange={(e) => setToolSchema(e.target.value)}
                        placeholder="Enter JSON schema"
                        rows={3}
                        disabled={true}
                        className={errors.toolSchema ? "p-invalid" : ""}
                    />
                    {errors.toolSchema && <small className="p-error">{errors.toolSchema}</small>}
                    {schemaError && <small className="p-error">{schemaError}</small>}
                </div>
            ) : (
                <div className="form-group">
                    <label htmlFor="apiEndpoint">
                        {ENDPOINT_LABELS[selectedConnector]} <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                        id="apiEndpoint"
                        value={apiEndpoint}
                        onChange={(e) => setApiEndpoint(e.target.value)}
                        placeholder={ENDPOINT_PLACEHOLDERS[selectedConnector]}
                        className={errors.apiEndpoint ? "p-invalid" : ""}
                    />
                    {errors.apiEndpoint && <small className="p-error">{errors.apiEndpoint}</small>}
                    {helpText && !errors.apiEndpoint && (
                        <small style={{ color: "#6c757d" }}>{helpText}</small>
                    )}
                </div>
            )}

            {/* ── Blob Storage: optional default container ── */}
            {selectedConnector === "blob-storage" && (
                <div className="form-group">
                    <label htmlFor="blobContainerName">
                        Default Container <span style={{ color: "#6c757d", fontSize: "0.85em" }}>(Optional)</span>
                    </label>
                    <InputText
                        id="blobContainerName"
                        value={blobContainerName || ""}
                        onChange={(e) => setBlobContainerName && setBlobContainerName(e.target.value)}
                        placeholder="e.g. my-data-container"
                    />
                    <small style={{ color: "#6c757d" }}>
                        Leave blank to allow access to all containers.
                    </small>
                </div>
            )}
 
            {/* ── Databricks: access token ── */}
            {selectedConnector === "databricks" && (
                <div className="form-group">
                    <label htmlFor="apiKey">Access Token <span style={{ color: "red" }}>*</span></label>
                    <InputText
                        id="apiKey"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="Enter Databricks access token"
                    />
                </div>
            )}
 
            {/* ── REST API: auth options ── */}
            {selectedConnector === "rest-api" && (
                <>
                    <div className="form-group">
                        <label>Authentication Type</label>
                        <RadioGroup
                            value={authType}
                            onChange={(e) => setAuthType(e.target.value)}
                            row
                        >
                            <FormControlLabel value="apiKey" control={<Radio />} label="API Key" />
                            <FormControlLabel value="oauth" control={<Radio />} label="OAuth" />
                            <FormControlLabel value="none" control={<Radio />} label="none" />
                        </RadioGroup>
                    </div>
 
                    {authType === "apiKey" && (
                        <div className="form-group">
                            <label htmlFor="apiKey">API Key</label>
                            <InputText
                                id="apiKey"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="Enter API Key"
                            />
                        </div>
                    )}
 
                    {authType === "oauth" && (
                        <div className="form-group">
                            <p>OAuth configuration options will go here.</p>
                        </div>
                    )}
                </>
            )}
 
            <div className="dialog-actions">
                <Button label="Save" icon="pi pi-check" className="p-button-success" onClick={handleSave} />
            </div>
        </Dialog>
    );
};
 
export default CustomToolDialog;
